import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Search, Briefcase, ChevronRight, CheckCircle2, Plus, Pencil, Trash2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddAdviserModal from '@/components/modals/AddAdviserModal';
import { toast } from 'sonner';

export default function ClientAdvisers() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [advisers, setAdvisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAdviser, setEditingAdviser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchAdvisers = async () => {
    if (!token || !clientId) return;
    try {
      const response = await api.getAdvisers(token, clientId);
      setAdvisers(response.data);
    } catch (error) {
      console.error('Failed to fetch advisers:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAdvisers();
  }, [token, clientId]);

  const handleAddAdviser = async (data) => {
    try {
      await api.createAdviser(token, clientId, data);
      toast.success('Adviser added successfully');
      await fetchAdvisers();
    } catch (error) {
      console.error('Failed to add adviser:', error);
      toast.error('Failed to add adviser');
      throw error;
    }
  };

  const handleEditAdviser = async (data) => {
    try {
      await api.updateAdviser(token, clientId, editingAdviser.id, data);
      toast.success('Adviser updated successfully');
      await fetchAdvisers();
      setEditingAdviser(null);
    } catch (error) {
      console.error('Failed to update adviser:', error);
      toast.error('Failed to update adviser');
      throw error;
    }
  };

  const handleDeleteAdviser = async (adviser) => {
    try {
      await api.deleteAdviser(token, clientId, adviser.id);
      toast.success('Adviser deleted successfully');
      await fetchAdvisers();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete adviser:', error);
      toast.error('Failed to delete adviser');
    }
  };

  const filteredAdvisers = advisers.filter(adv => {
    // Support both new format (firstName/lastName) and legacy (name)
    const fullName = adv.firstName && adv.lastName 
      ? `${adv.firstName} ${adv.middleName || ''} ${adv.lastName}`.toLowerCase()
      : (adv.name || '').toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) ||
           adv.organisation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           adv.roleCategory?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="advisers-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Advisers</h1>
          <p className="text-[#5D524D]">{filteredAdvisers.length} advisers for {client?.name}</p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="caffeine-btn"
          data-testid="add-adviser-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Adviser
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
          <Input
            placeholder="Search by name, organisation, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="caffeine-input pl-10"
            data-testid="advisers-search-input"
          />
        </div>
      </div>

      {/* Advisers Table */}
      {filteredAdvisers.length > 0 ? (
        <div className="caffeine-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role Category</th>
                <th>Organisation</th>
                <th>Department</th>
                <th>Type</th>
                <th>Primary</th>
                <th>Last Verified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAdvisers.map(adv => {
                // Get display name with preferred name support
                const displayName = adv.firstName && adv.lastName 
                  ? `${adv.firstName}${adv.middleName ? ' ' + adv.middleName : ''} ${adv.lastName}`
                  : adv.name;
                const initials = adv.firstName && adv.lastName
                  ? `${adv.firstName[0]}${adv.lastName[0]}`
                  : adv.name?.split(' ').map(n => n[0]).join('') || '??';
                
                return (
                <tr key={adv.id} data-testid={`adviser-row-${adv.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm font-medium">
                        {initials}
                      </div>
                      <div>
                        <span className="font-medium text-[#2C2420]">{displayName}</span>
                        {adv.preferredName && (
                          <span className="text-xs text-[#8C8078] ml-2">
                            ({adv.preferredName})
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      adv.roleCategory === 'Partner' ? 'bg-[#4A3B32]/10 text-[#4A3B32]' :
                      adv.roleCategory === 'Manager' ? 'bg-[#6B8E6B]/10 text-[#6B8E6B]' :
                      adv.roleCategory === 'Wealth' ? 'bg-[#C6A88F]/20 text-[#5D524D]' :
                      'bg-[#F0EBE6] text-[#5D524D]'
                    }`}>
                      {adv.roleCategory}
                    </span>
                  </td>
                  <td>{adv.organisation}</td>
                  <td>{adv.department}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      adv.type === 'Internal' ? 'bg-[#4A3B32]/10 text-[#4A3B32]' : 'bg-[#F0EBE6] text-[#5D524D]'
                    }`}>
                      {adv.type}
                    </span>
                  </td>
                  <td>
                    {adv.primary ? (
                      <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" />
                    ) : (
                      <span className="text-[#8C8078]">—</span>
                    )}
                  </td>
                  <td>{formatDateAU(adv.lastVerified)}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingAdviser(adv)}
                        className="caffeine-btn-ghost p-2"
                        data-testid={`edit-adviser-${adv.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setDeleteConfirm(adv)}
                        className="caffeine-btn-ghost p-2 text-red-600 hover:text-red-700"
                        data-testid={`delete-adviser-${adv.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/app/clients/${clientId}/advisers/${adv.id}`)}
                        className="caffeine-btn-ghost"
                        data-testid={`view-adviser-${adv.id}`}
                      >
                        View
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Briefcase className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No advisers found</p>
          <p className="text-sm">Try adjusting your search</p>
        </div>
      )}

      {/* Add Adviser Modal */}
      <AddAdviserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddAdviser}
        clientId={clientId}
      />

      {/* Edit Adviser Modal */}
      <AddAdviserModal
        isOpen={!!editingAdviser}
        onClose={() => setEditingAdviser(null)}
        onSave={handleEditAdviser}
        clientId={clientId}
        initialData={editingAdviser}
        isEdit={true}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Adviser</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteConfirm?.firstName} {deleteConfirm?.lastName || deleteConfirm?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteAdviser(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
